import {
  BoxGeometry,
  CanvasTexture,
  Color,
  Curve,
  DoubleSide,
  Group,
  LatheGeometry,
  LinearMipmapLinearFilter,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  SphereGeometry,
  SRGBColorSpace,
  type Texture,
  TubeGeometry,
  Vector2,
  Vector3,
  type ShaderMaterial,
} from 'three';
import type { Rect } from '../art/gazette.ts';
import { BRASS, THREAD } from '../art/palette.ts';
import { shadowMaterial } from './materials.ts';

export function canvasTexture(canvas: HTMLCanvasElement): CanvasTexture {
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.anisotropy = 4;
  return texture;
}

export interface CardSource {
  texture: Texture;
  /** World width; height follows the source aspect. */
  width: number;
  aspect: number;
  /** Sub-rectangle of the texture, in canvas UV space (origin top-left). */
  region?: Rect;
}

const PIN_GEOMETRY = new SphereGeometry(0.075, 18, 12);
const PIN_MATERIAL = new MeshStandardMaterial({ color: new Color(BRASS), metalness: 0.65, roughness: 0.32 });

/**
 * A card pinned to the board: fiche or clipping. The group sits on the board; `body` is what
 * lifts, flips and tilts, so the halftone shadow can stay on the felt and spread with lift.
 */
export class CardItem {
  readonly group = new Group();
  readonly body = new Group();
  readonly card: Mesh<PlaneGeometry, MeshBasicMaterial>;
  readonly pin = new Mesh(PIN_GEOMETRY, PIN_MATERIAL);
  private readonly shadow: Mesh<PlaneGeometry, ShaderMaterial>;
  width = 1;
  height = 1;
  private liftValue = 0;
  private ownTexture: Texture | null = null;

  constructor(source: CardSource, pixelRatio: number, ownsTexture = false) {
    this.card = new Mesh(new PlaneGeometry(1, 1), new MeshBasicMaterial({ side: DoubleSide, transparent: true }));
    this.shadow = new Mesh(new PlaneGeometry(1, 1), shadowMaterial(pixelRatio, new Vector2(1, 1)));
    this.shadow.renderOrder = -1;
    this.body.add(this.card, this.pin);
    this.group.add(this.shadow, this.body);
    this.setSource(source, ownsTexture);
    this.setLift(0);
  }

  setSource(source: CardSource, ownsTexture = false): void {
    if (this.ownTexture && this.ownTexture !== source.texture) this.ownTexture.dispose();
    this.ownTexture = ownsTexture ? source.texture : null;
    this.card.material.map = source.texture;
    this.card.material.needsUpdate = true;
    this.card.geometry.dispose();
    this.card.geometry = new PlaneGeometry(1, 1);
    if (source.region) {
      const { x, y, w, h } = source.region;
      const uv = this.card.geometry.attributes.uv!;
      for (let i = 0; i < uv.count; i++) {
        uv.setXY(i, x + uv.getX(i) * w, 1 - y - (1 - uv.getY(i)) * h);
      }
      uv.needsUpdate = true;
    }
    this.setSize(source.width, source.width / source.aspect);
  }

  setSize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.card.scale.set(width, height, 1);
    this.pin.position.set(0, height / 2 - Math.min(0.16, height * 0.12), 0.05);
    this.shadow.scale.set(width + 0.9, height + 0.9, 1);
    this.shadow.material.uniforms.uSize!.value.set(width, height);
  }

  setLift(lift: number): void {
    this.liftValue = lift;
    this.body.position.z = 0.02 + lift;
    this.shadow.position.set(0.05 + lift * 0.22, -0.08 - lift * 0.32, 0.002);
    this.shadow.material.uniforms.uLift!.value = Math.min(1, lift);
  }

  get lift(): number {
    return this.liftValue;
  }

  setOpacity(opacity: number): void {
    this.card.material.opacity = opacity;
    this.shadow.material.uniforms.uOpacity!.value = opacity;
    this.pin.visible = opacity > 0.5;
  }

  /** Multiplies the card colors down; used to push other cases into the background. */
  setDim(amount: number): void {
    this.card.material.color.setScalar(amount);
  }

  showPin(visible: boolean): void {
    this.pin.visible = visible;
  }

  pinWorldPosition(target = new Vector3()): Vector3 {
    return this.pin.getWorldPosition(target);
  }

  bounds(): { x: number; y: number; w: number; h: number } {
    return { x: this.group.position.x, y: this.group.position.y, w: this.width, h: this.height };
  }

  dispose(): void {
    this.card.geometry.dispose();
    this.card.material.dispose();
    this.shadow.geometry.dispose();
    this.shadow.material.dispose();
    this.ownTexture?.dispose();
    for (const child of [...this.body.children]) {
      if (child !== this.card && child !== this.pin && child instanceof Mesh) {
        child.geometry.dispose();
        (child.material as MeshBasicMaterial).dispose();
      }
    }
    this.group.removeFromParent();
  }
}

/** A decal inked onto a card (stamp impression). */
export function decal(texture: Texture, width: number, aspect: number): Mesh<PlaneGeometry, MeshBasicMaterial> {
  const mesh = new Mesh(
    new PlaneGeometry(width, width / aspect),
    new MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false, opacity: 0.92 }),
  );
  mesh.position.z = 0.004;
  return mesh;
}

class SagCurve extends Curve<Vector3> {
  readonly from = new Vector3();
  readonly to = new Vector3();
  sag = 0.2;
  progress = 1;

  // Three's typings mark Curve's constructor protected; subclasses must expose their own.
  constructor() {
    super();
  }

  override getPoint(t: number, target = new Vector3()): Vector3 {
    const u = t * this.progress;
    const control = new Vector3().addVectors(this.from, this.to).multiplyScalar(0.5);
    control.y -= this.sag;
    control.z += 0.12;
    const a = 1 - u;
    return target.set(
      a * a * this.from.x + 2 * a * u * control.x + u * u * this.to.x,
      a * a * this.from.y + 2 * a * u * control.y + u * u * this.to.y,
      a * a * this.from.z + 2 * a * u * control.z + u * u * this.to.z,
    );
  }
}

/** Thick enough to read at arm's length when the whole board is in frame. */
const THREAD_RADIUS = 0.045;

// Transparent from the start: flipping the flag later needs a shader recompile, so a fade would be ignored.
const THREAD_MATERIAL = new MeshStandardMaterial({ color: new Color(THREAD), roughness: 0.85, metalness: 0, transparent: true });

/** The red thread of an accusation, strung between two pins. */
export class Thread {
  readonly mesh: Mesh<TubeGeometry, MeshStandardMaterial>;
  private readonly curve = new SagCurve();

  constructor(readonly suspectId: string) {
    this.mesh = new Mesh(new TubeGeometry(this.curve, 2, 0.02, 5), THREAD_MATERIAL.clone());
  }

  get from(): Vector3 {
    return this.curve.from;
  }

  get to(): Vector3 {
    return this.curve.to;
  }

  set(from: Vector3, to: Vector3, sag: number, progress = 1): void {
    this.curve.from.copy(from);
    this.curve.to.copy(to);
    this.curve.sag = sag;
    this.curve.progress = Math.max(0.001, progress);
    this.rebuild();
  }

  update(changes: { sag?: number; progress?: number; from?: Vector3; to?: Vector3 }): void {
    if (changes.sag !== undefined) this.curve.sag = changes.sag;
    if (changes.progress !== undefined) this.curve.progress = Math.max(0.001, changes.progress);
    if (changes.from) this.curve.from.copy(changes.from);
    if (changes.to) this.curve.to.copy(changes.to);
    this.rebuild();
  }

  setOpacity(opacity: number): void {
    this.mesh.material.opacity = opacity;
  }

  get sag(): number {
    return this.curve.sag;
  }

  private rebuild(): void {
    // Curve caches its arc lengths; they go stale every time the ends or the sag move.
    this.curve.updateArcLengths();
    this.mesh.geometry.dispose();
    this.mesh.geometry = new TubeGeometry(this.curve, 36, THREAD_RADIUS, 6);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.mesh.removeFromParent();
  }
}

/** The rubber stamp that comes down on a card at the reveal. */
export function rubberStamp(): Group {
  const group = new Group();
  const wood = new MeshStandardMaterial({ color: new Color('#6e4a2c'), roughness: 0.55 });
  const handle = new Mesh(
    new LatheGeometry(
      [
        [0, 0],
        [0.2, 0],
        [0.22, 0.06],
        [0.12, 0.22],
        [0.1, 0.46],
        [0.24, 0.64],
        [0.26, 0.74],
        [0.18, 0.84],
        [0, 0.86],
      ].map(([x, y]) => new Vector2(x, y)),
      28,
    ),
    wood,
  );
  handle.rotation.x = Math.PI / 2;
  handle.position.z = 0.2;
  const block = new Mesh(new BoxGeometry(1.5, 0.62, 0.2), new MeshStandardMaterial({ color: new Color('#4a2f1c'), roughness: 0.6 }));
  block.position.z = 0.1;
  const rubber = new Mesh(new BoxGeometry(1.42, 0.54, 0.05), new MeshStandardMaterial({ color: new Color('#a82a12'), roughness: 0.9 }));
  rubber.position.z = -0.02;
  group.add(handle, block, rubber);
  group.rotation.x = 0.28;
  return group;
}
